import {Component, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {websiteLinksResourceType} from "./configs/mb-website-links-modal-config";
import {WebsiteLinkData, WebsiteLinkFormField, WebsiteLinksDialogData} from "./types/mb-website-links-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {defaultWebsiteLinksData} from "./configs/default-website-link-data";
import {websiteLinkFormFields} from "./configs/website-link-form-fields";
import {MbWebsiteLinksModalService} from "./services/mb-website-links-modal.service";
import {MatIcon} from "@angular/material/icon";
import {MatMiniFabButton} from "@angular/material/button";
import {WebsiteLinkFormComponent} from "./components/website-link-form/website-link-form.component";

@Component({
  selector: 'app-mb-website-links-modal',
  imports: [
    BaseModalComponent,
    MatIcon,
    MatMiniFabButton,
    WebsiteLinkFormComponent
  ],
  templateUrl: './mb-website-links-modal.component.html',
  styleUrl: './mb-website-links-modal.component.scss'
})
export class MbWebsiteLinksModalComponent extends BaseModalComponent {
  public websiteLinksDialogData = inject<WebsiteLinksDialogData>(MAT_DIALOG_DATA);
  private mbWebsiteLinksModalService: MbWebsiteLinksModalService = inject(MbWebsiteLinksModalService);

  public readonly websiteLinksResourceType = websiteLinksResourceType;

  public websiteLinksData = signal<Array<WebsiteLinkData>>([]);
  public websiteLinkFormGroups = signal<Array<FormGroup>>([]);
  public noFirstData = signal<boolean>(false);
  public isValid = signal<boolean>(false);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.websiteLinksDialogData.modalKey);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<WebsiteLinkFormField>(websiteLinkFormFields);
    this.websiteLinkFormGroups.update(forms  => [ ...forms, formGroup]);
    this.websiteLinksData.update(data  => [ ...data, defaultWebsiteLinksData ]);
    this.isValid.set(false);
  }

  public updateWebsiteLinkData(websiteLinkData: WebsiteLinkData, index: number): void {
    this.websiteLinksData()[index] = websiteLinkData;
    this.websiteLinkFormGroups().at(index)?.patchValue(websiteLinkData);
    this.isValid.set(!!websiteLinkData.id);
    this.noFirstData.set(!!this.websiteLinksData().length);
  }

  public deleteWebsiteLinkData(index: number): void {
    !this.isValid && this.handleAddNew();
    this.websiteLinksData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.websiteLinkFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(!!this.websiteLinksData().length);
    this.noFirstData.set(!!this.websiteLinksData().length);
    !this.isValid() && this.handleAddNew();
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(this.websiteLinksDialogData.modalKey);
    await this.loadWebsiteLinkData();
    this.isValid.set(!!this.websiteLinksData().at(0)?.id);
    this.noFirstData.set(!!this.websiteLinksData().at(0)?.id);
  }

  private async loadWebsiteLinkData(): Promise<void> {
    const params = this.mbWebsiteLinksModalService.setDataInModalParams(this.websiteLinksDialogData.parentId);
    await this.mbWebsiteLinksModalService.getDataInModal<WebsiteLinkData>(params)
      .then((data) => this.websiteLinksData.set(data));
    this.initForm();
  }

  private initForm(): void {
    if (!this.websiteLinksData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.websiteLinksData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<WebsiteLinkFormField>(websiteLinkFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.websiteLinkFormGroups.set(formGroups);
  }
}
